/**
 * PrescreenTab — the study's phone prescreen questionnaire.
 *
 * Top: the PUBLISHED prescreen (immutable snapshot recruiters run during
 * calls; question_keys frozen at publish). Below (admin): the latest
 * prescreen_draft artifact — AI-drafted from the protocol's I/E criteria,
 * reviewed/edited question-by-question (QuestionEditor), previewable via the
 * shared FormRenderer, then published as the next immutable version.
 */
import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Calculator, Eye, Loader2, Pencil, Plus, Sparkles } from 'lucide-react';
import api from '../../../services/api';
import { DraftCriterion, PrescreenCriterion, StudyDetailResponse } from '../../types';
import { relTime, ruleSummaryText, resolveCalculations, studyHasExtractedDoc } from '../../lib';
import { useConfirm } from '../ui';
import FormRenderer, { FormQuestion } from '../forms/FormRenderer';
import QuestionEditor from '../forms/QuestionEditor';
import { latestArtifact } from './TextArtifactPanel';

/** A stable draft-local id for a criterion (wires calculation inputs across
 *  reorders); minted lazily so AI-drafted and legacy criteria get one too. */
const mintRef = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `c-${Math.random().toString(36).slice(2)}`);

/** Human description of an eligibility rule for the read-only views (handles
 *  both a single leaf and an AND/OR group via the shared lib formatter). */
function ruleSummary(rule: any, answerType: string): string {
  const s = ruleSummaryText(rule, answerType);
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Recruiter judgment';
}

const kindChip = (kind: string) =>
  kind === 'exclusion'
    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';

type PrescreenLength = 'short' | 'medium' | 'long';
const LENGTH_OPTIONS: { value: PrescreenLength; label: string; hint: string }[] = [
  { value: 'short', label: 'Short', hint: 'Only the highest-yield knock-outs (~5–8 questions). Fastest calls.' },
  { value: 'medium', label: 'Medium', hint: 'The decision-relevant criteria, merged (~10–20 questions). Recommended.' },
  { value: 'long', label: 'Full', hint: 'Every distinct screening criterion (up to the cap). Most thorough.' },
];

function PublishedCriterionRow({ c, index }: { c: PrescreenCriterion; index: number }) {
  const [showSource, setShowSource] = useState(false);
  return (
    <li className="py-3">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 w-6 flex-shrink-0 text-right text-xs font-medium text-gray-400">{index + 1}.</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{c.display_question}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
            <span className={`rounded-full px-2 py-0.5 font-medium ${kindChip(c.criterion_kind)}`}>{c.criterion_kind}</span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600 dark:bg-gray-700 dark:text-gray-300">{c.category}</span>
            {c.calculation && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                <Calculator className="h-3 w-3" /> computed {c.calculation.preset}
              </span>
            )}
            <span className="text-gray-500 dark:text-gray-400">{ruleSummary(c.rule, c.answer_type)}</span>
            {c.profile_key && <span className="font-mono text-gray-400 dark:text-gray-500">{c.profile_key}</span>}
            <button
              type="button"
              onClick={() => setShowSource((s) => !s)}
              className="font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400"
            >
              {showSource ? 'Hide source' : 'Source'}
            </button>
          </div>
          {showSource && (
            <p className="mt-1.5 rounded bg-gray-50 p-2 text-xs italic text-gray-600 dark:bg-gray-900/50 dark:text-gray-400">
              "{c.source_text}"
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

export default function PrescreenTab({ detail, isAdmin, onChanged }: { detail: StudyDetailResponse; isAdmin: boolean; onChanged: () => void }) {
  const confirm = useConfirm();
  const latest = latestArtifact(detail.artifacts, 'prescreen_draft');
  // Only a working (generating/draft/failed) latest is an OPEN draft. A settled
  // latest — published, or dismissed (→ superseded) — shows no draft workspace,
  // so the AI stops nudging to replace the published prescreen until you draft anew.
  const draft = latest && ['generating', 'draft', 'failed'].includes(latest.status) ? latest : null;
  const [criteria, setCriteria] = useState<DraftCriterion[]>([]);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(false);
  const [previewAnswers, setPreviewAnswers] = useState<Record<string, any>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [length, setLength] = useState<PrescreenLength>('medium');
  // Optional free-text direction for the AI: `genInstructions` steers a fresh
  // "Draft a new version" build; `reviseInstructions` drives the in-place "Ask
  // AI to revise" of the open draft. Both capped to match the backend.
  const [genInstructions, setGenInstructions] = useState('');
  const [showGenInstructions, setShowGenInstructions] = useState(false);
  const [reviseInstructions, setReviseInstructions] = useState('');
  const MAX_INSTRUCTIONS = 20000;
  // Collapse the (often long) published list while an admin works a draft, so the
  // draft workspace below is reachable without scrolling past every approved
  // question. Toggleable; resynced when the draft identity/status flips below.
  const draftInProgress = draft?.status === 'draft' || draft?.status === 'generating';
  const [showPublished, setShowPublished] = useState(!draftInProgress);
  const hasExtractedDoc = studyHasExtractedDoc(detail);
  // Offer a no-AI hand-edit of the live prescreen when one is published and no
  // draft is mid-flight (fork its source artifact → a fresh draft to tweak).
  const canEditPublished = isAdmin && !!detail.prescreen?.source_artifact_id && !draftInProgress;

  // Sync the editor only when a DIFFERENT draft (or status flip) arrives.
  // Depending on the whole `draft` object would reseed on every detail poll
  // and wipe in-progress edits — id+status is the deliberate identity here.
  useEffect(() => {
    const loaded = Array.isArray(draft?.content?.criteria) ? draft!.content.criteria : [];
    // Backfill a stable ref on any criterion missing one (AI drafts + legacy)
    // so calculation inputs can reference siblings; persisted on the next save.
    setCriteria(loaded.map((c: DraftCriterion) => (c.ref ? c : { ...c, ref: mintRef() })));
    setDirty(false);
    setPreview(false);
    setPreviewAnswers({});
    setError(null);
    setReviseInstructions(''); // a new draft / status flip clears the revise box
    setShowPublished(!draftInProgress); // collapse the approved list while editing a draft
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id, draft?.status]);

  // Preview keys answers by the stable ref (not array index) so reordering and
  // calculation input wiring stay correct; calc rows are shown read-only below.
  const previewQuestions: FormQuestion[] = useMemo(
    () => criteria.filter((c) => !c.calculation).map((c) => ({
      key: c.ref || '',
      label: c.display_question,
      type: c.answer_type,
      options: c.options,
    })),
    [criteria]
  );
  const previewResolved = useMemo(
    () => resolveCalculations(criteria.map((c) => ({ question_key: c.ref || '', answer_type: c.answer_type, calculation: c.calculation })), previewAnswers),
    [criteria, previewAnswers]
  );

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
      // Await the reload so `busy` stays set until fresh detail lands, keeping
      // the buttons disabled through the refetch gap (a fast double-click on
      // "Edit" would otherwise re-fire the fork before the draft flips in).
      await onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const generate = async () => {
    // Regenerating past an existing draft version-bumps it into oblivion —
    // make sure that's what the admin wants when edits exist.
    if (draft?.status === 'draft'
        && !(await confirm({
          title: 'Discard the current draft (including any edits) and generate a fresh one from the protocol?',
          confirmLabel: 'Discard and regenerate',
          danger: true,
        }))) {
      return;
    }
    run('Generate', () => api.post(`/api/crm/studies/${detail.study.id}/generate`, {
      types: ['prescreen_draft'],
      length,
      instructions: genInstructions.trim() || undefined,
    }));
  };
  // Hand-edit the live prescreen: fork the published version into a new editable
  // draft (no AI), seeded with its exact questions. The reload drops the new
  // prescreen_draft into the workspace below for review + republish.
  const editPublished = () =>
    run('Edit', () => api.post(`/api/crm/artifacts/${detail.prescreen!.source_artifact_id}/revise`, {}));
  // Ask AI to revise the OPEN draft from a plain-language instruction. Persist
  // any unsaved hand-edits first (so the AI revises exactly what's on screen),
  // then kick off the revise; the reload flips the draft to a generating spinner.
  const aiRevise = () => {
    const instr = reviseInstructions.trim();
    if (!instr) { setError('Tell the AI what to change'); return; }
    run('Revise', async () => {
      if (dirty) {
        await api.patch(`/api/crm/artifacts/${draft!.id}`, { content: { criteria } });
        setDirty(false);
      }
      await api.post(`/api/crm/artifacts/${draft!.id}/ai-revise`, { instructions: instr });
      setReviseInstructions('');
    });
  };
  const saveDraft = () => run('Save', async () => {
    await api.patch(`/api/crm/artifacts/${draft!.id}`, { content: { criteria } });
    setDirty(false);
  });
  const publish = async () => {
    if (dirty) {
      setError('Save the draft before publishing');
      return;
    }
    if (!(await confirm({
      title: `Publish prescreen v${(detail.prescreen?.version || 0) + 1}?`,
      message: 'Question keys freeze at publish — later edits create a new version.',
      confirmLabel: 'Publish',
    }))) return;
    run('Publish', () => api.post(`/api/crm/artifacts/${draft!.id}/publish`, {}));
  };
  // Set the draft aside (no new version) so it stops nudging to replace the
  // published prescreen. Keeps the live version; you can draft anew anytime.
  const discard = async () => {
    if (draft!.status === 'draft'
        && !(await confirm({
          title: 'Dismiss this draft and keep the published prescreen?',
          message: 'Any unsaved edits are discarded — you can draft a new version anytime.',
          confirmLabel: 'Dismiss draft',
          danger: true,
        }))) return;
    run('Discard', () => api.post(`/api/crm/artifacts/${draft!.id}/discard`, {}));
  };

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= criteria.length) return;
    const next = [...criteria];
    [next[i], next[j]] = [next[j], next[i]];
    setCriteria(next);
    setDirty(true);
  };
  const update = (i: number, c: DraftCriterion) => {
    setCriteria((prev) => prev.map((p, idx) => (idx === i ? c : p)));
    setDirty(true);
  };
  const remove = (i: number) => {
    setCriteria((prev) => prev.filter((_, idx) => idx !== i));
    setDirty(true);
  };
  const add = () => {
    setCriteria((prev) => [...prev, {
      criterion_kind: 'inclusion',
      category: 'other',
      source_text: 'Added manually',
      display_question: '',
      answer_type: 'boolean',
      options: null,
      rule: {},
      profile_key_suggestion: null,
      ref: mintRef(),
    }]);
    setDirty(true);
  };
  const addCalc = () => {
    setCriteria((prev) => [...prev, {
      criterion_kind: 'inclusion',
      category: 'age',
      source_text: 'Added manually',
      display_question: 'Age',
      answer_type: 'number',
      options: null,
      rule: {},
      calculation: { preset: 'age', inputs: {} },
      profile_key_suggestion: null,
      ref: mintRef(),
    }]);
    setDirty(true);
  };

  const generateBtn = (label: string) => (
    <button
      type="button"
      onClick={generate}
      disabled={busy !== null || !hasExtractedDoc}
      title={!hasExtractedDoc ? 'Upload a protocol in Documents first' : undefined}
      className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Sparkles className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* ---- Published prescreen (what recruiters run) ---- */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Published prescreen</h2>
            {detail.prescreen && (
              <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                v{detail.prescreen.version} · {detail.prescreen.criteria.length} questions · published {relTime(detail.prescreen.published_at)} ago
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canEditPublished && (
              <button
                type="button"
                onClick={editPublished}
                disabled={busy !== null}
                title="Edit the published prescreen by hand (no AI) and publish it as a new version"
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <Pencil className="h-3.5 w-3.5" /> {busy === 'Edit' ? 'Opening…' : 'Edit'}
              </button>
            )}
            {detail.prescreen && (
              <button
                type="button"
                onClick={() => setShowPublished((s) => !s)}
                aria-expanded={showPublished}
                className="text-sm font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400"
              >
                {showPublished ? 'Hide questions' : 'Show questions'}
              </button>
            )}
          </div>
        </div>
        {!detail.prescreen ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No published prescreen yet.{isAdmin ? ' Generate a draft below, review it, and publish.' : ' An admin needs to publish one.'}
          </p>
        ) : showPublished ? (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {detail.prescreen.criteria.map((c, i) => (
              <PublishedCriterionRow key={c.id} c={c} index={i} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {detail.prescreen.criteria.length} published question{detail.prescreen.criteria.length === 1 ? '' : 's'} hidden so you can focus on the draft below.{' '}
            <button type="button" onClick={() => setShowPublished(true)} className="font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400">Show</button>
          </p>
        )}
      </div>

      {/* ---- Draft workspace (admin) ---- */}
      {isAdmin && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Draft workspace</h2>
              {draft?.status === 'draft' && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                  {dirty ? 'Unsaved changes' : 'Draft — review before publishing'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {draft?.status === 'draft' && criteria.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setPreview((p) => !p)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <Eye className="h-3.5 w-3.5" /> {preview ? 'Edit' : 'Preview'}
                  </button>
                  <button
                    type="button"
                    onClick={saveDraft}
                    disabled={busy !== null || !dirty}
                    className="rounded-lg border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 disabled:opacity-50 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                  >
                    {busy === 'Save' ? 'Saving…' : 'Save draft'}
                  </button>
                  <button
                    type="button"
                    onClick={publish}
                    disabled={busy !== null || dirty}
                    title={dirty ? 'Save the draft first' : undefined}
                    className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {busy === 'Publish' ? 'Publishing…' : `Publish as v${(detail.prescreen?.version || 0) + 1}`}
                  </button>
                </>
              )}
              {(!draft || draft.status !== 'generating') && (
                <div className="inline-flex items-center gap-1.5" role="group" aria-label="Prescreen length">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Length</span>
                  <div className="inline-flex overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600">
                    {LENGTH_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setLength(opt.value)}
                        aria-pressed={length === opt.value}
                        title={opt.hint}
                        className={`px-2.5 py-1.5 text-xs font-medium transition-colors duration-150 ${
                          length === opt.value
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                        }`}
                      >
                        {opt.label}
                        {opt.value === 'medium' && <span className="ml-1 opacity-70">★</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {draft && draft.status !== 'generating' && (
                <button
                  type="button"
                  onClick={discard}
                  disabled={busy !== null}
                  title="Dismiss this draft and keep the published prescreen"
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {busy === 'Discard' ? 'Dismissing…' : 'Dismiss draft'}
                </button>
              )}
              {!draft && generateBtn(detail.prescreen ? 'Draft a new version' : 'Draft from protocol')}
              {draft && draft.status !== 'generating' && generateBtn('Draft a new version')}
            </div>
          </div>

          {/* Optional free-text direction the AI follows when you draft a new
              version (so the build isn't a black box). Collapsed by default. */}
          {(!draft || draft.status !== 'generating') && (
            <div className="mb-3">
              {showGenInstructions ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                  <div className="mb-1 flex items-center justify-between">
                    <label htmlFor="ps-gen-instructions" className="text-xs font-medium text-gray-600 dark:text-gray-300">
                      Direction for the AI <span className="text-gray-400">(optional)</span> — used when you draft a new version
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowGenInstructions(false)}
                      className="text-xs font-medium text-gray-400 transition-colors duration-150 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                      Hide
                    </button>
                  </div>
                  <textarea
                    id="ps-gen-instructions"
                    rows={2}
                    value={genInstructions}
                    onChange={(e) => setGenInstructions(e.target.value)}
                    maxLength={MAX_INSTRUCTIONS}
                    placeholder="e.g. Focus on the cardiac exclusions. Keep it under 10 questions. Ask about insulin use specifically."
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowGenInstructions(true)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400"
                >
                  <Sparkles className="h-3 w-3" /> Add direction for the AI{genInstructions.trim() ? ' (set)' : ''}
                </button>
              )}
            </div>
          )}

          {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

          {draft?.status === 'generating' && (
            <div className="flex items-center gap-3 rounded-lg bg-primary-50 p-4 dark:bg-primary-900/20">
              <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
              <p className="text-sm text-primary-800 dark:text-primary-200">
                {draft.generation_options?.ai_revise
                  ? 'Revising the questions based on your instructions (reading the protocol to ground any changes)…'
                  : "Reading the protocol's inclusion/exclusion criteria and drafting plain-language questions…"}
              </p>
            </div>
          )}
          {draft?.status === 'failed' && (
            <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              Draft failed: {draft.error || 'unknown error'}
            </p>
          )}
          {!draft && (
            latest?.status === 'published' ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This draft was published{detail.prescreen ? ` as v${detail.prescreen.version}` : ''}. Draft a new version to make changes.
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                The AI reads the protocol's inclusion/exclusion criteria and drafts a phone questionnaire in plain
                language. You review and edit every question before anything goes live.
              </p>
            )
          )}

          {draft?.status === 'draft' && (
            <div className="space-y-3">
              {/* Ask AI to revise the open draft in plain language — the AI edits
                  the questions below (grounded in the protocol) and you review
                  before publishing. Visible in both preview and edit modes. */}
              <div className="rounded-lg border border-primary-200 bg-primary-50/60 p-3 dark:border-primary-800/60 dark:bg-primary-900/15">
                <label htmlFor="ps-revise-instructions" className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-primary-800 dark:text-primary-200">
                  <Sparkles className="h-3.5 w-3.5" /> Ask AI to revise these questions
                </label>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  Tell the AI what to change in plain language — it edits the questions below using the study's protocol, and you review every change before publishing.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <textarea
                    id="ps-revise-instructions"
                    rows={2}
                    value={reviseInstructions}
                    onChange={(e) => setReviseInstructions(e.target.value)}
                    maxLength={MAX_INSTRUCTIONS}
                    placeholder="e.g. Add a question about kidney problems. Make question 3 a yes/no. Remove the smoking question."
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  />
                  <button
                    type="button"
                    onClick={aiRevise}
                    disabled={busy !== null || !reviseInstructions.trim()}
                    className="inline-flex h-fit items-center gap-1.5 self-start rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Sparkles className="h-4 w-4" /> {busy === 'Revise' ? 'Revising…' : 'Revise with AI'}
                  </button>
                </div>
              </div>

              {preview ? (
              <div className="mx-auto max-w-lg rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <p className="mb-4 text-xs font-medium uppercase tracking-wide text-gray-400">Recruiter view preview</p>
                <FormRenderer
                  questions={previewQuestions}
                  answers={previewAnswers}
                  onAnswer={(key, value) => setPreviewAnswers((a) => ({ ...a, [key]: value }))}
                />
                {criteria.some((c) => c.calculation) && (
                  <div className="mt-5 space-y-1.5 border-t border-gray-100 pt-4 dark:border-gray-700">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Computed</p>
                    {criteria.filter((c) => c.calculation).map((c) => {
                      const v = previewResolved[c.ref || ''];
                      return (
                        <div key={c.ref} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 dark:text-gray-200">{c.display_question || `(${c.calculation!.preset})`}</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{v === undefined || v === null ? '—' : v}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {criteria.map((c, i) => (
                  <div key={c.ref || i} className="flex gap-2">
                    <div className="flex flex-col gap-1 pt-3">
                      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"
                        className="rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 dark:hover:text-gray-200">
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <span className="text-center text-xs font-medium text-gray-400">{i + 1}</span>
                      <button type="button" onClick={() => move(i, 1)} disabled={i === criteria.length - 1} aria-label="Move down"
                        className="rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 dark:hover:text-gray-200">
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="min-w-0 flex-1">
                      <QuestionEditor
                        criterion={c}
                        siblings={criteria.filter((_, idx) => idx !== i)}
                        onChange={(next) => update(i, next)}
                        onRemove={() => remove(i)}
                      />
                    </div>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={add}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:border-primary-400 hover:text-primary-700 dark:border-gray-600 dark:text-gray-300"
                  >
                    <Plus className="h-4 w-4" /> Add question
                  </button>
                  <button
                    type="button"
                    onClick={addCalc}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-primary-300 px-3 py-2 text-sm font-medium text-primary-700 transition-colors duration-150 hover:border-primary-400 hover:bg-primary-50 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-900/20"
                  >
                    <Calculator className="h-4 w-4" /> Add calculation (age / BMI)
                  </button>
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
